import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { toast } from "sonner";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/forgot-password")({
  head: () =>
    buildSeo({
      path: "/forgot-password",
      title: "Forgot password — Reality Twin",
      description:
        "Reset your Reality Twin Network password — we'll email you a secure link to set a new one.",
    }),
  component: Page,
});

function Page() {
  const t = useHydratedTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) return toast.error(error.message);
    setSent(true);
  }

  return (
    <Layout>
      <section className="mx-auto max-w-md px-6 py-24">
        <h1 className="font-display text-4xl font-semibold text-gradient">
          {t("routes.auth.forgotTitle")}
        </h1>
        <p className="mt-3 text-muted-foreground text-sm">{t("routes.auth.forgotSub")}</p>
        {sent ? (
          <div className="mt-8 glass-strong rounded-2xl p-6">
            <p className="text-sm">
              {t("routes.auth.sentPre")}
              <span className="font-mono">{email}</span>
              {t("routes.auth.sentPost")}
            </p>
            <Link to="/auth" className="mt-4 inline-block text-xs text-[oklch(0.82_0.18_168)]">
              {t("routes.auth.back")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 glass-strong rounded-2xl p-6 space-y-4">
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1.5">
                {t("routes.auth.emailLabel")}
              </div>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
            </label>
            <button
              disabled={loading}
              type="submit"
              className="w-full rounded-full brand-gradient text-black py-2.5 text-sm font-medium disabled:opacity-50"
            >
              {loading ? t("routes.auth.sending") : t("routes.auth.send")}
            </button>
            <Link
              to="/auth"
              className="block text-center text-xs text-muted-foreground hover:text-white"
            >
              {t("routes.auth.back")}
            </Link>
          </form>
        )}
      </section>
    </Layout>
  );
}
